/**
 * @description HubSpot API service for form submissions and contact management
 * @api Provides methods for HubSpot form submissions and newsletter subscriptions
 * @security Handles external API calls with proper error handling
 * @since 1.0.0
 */

interface HubSpotField {
  name: string;
  value: string;
}

interface HubSpotFormData {
  fields: HubSpotField[];
  context: {
    pageUri: string;
    pageName: string;
  };
}

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}

interface NewsletterFormData {
  email: string;
}

/**
 * @description Service class for HubSpot API interactions
 * @api Handles form submissions to HubSpot forms
 * @example
 * ```typescript
 * const hubSpotService = new HubSpotService();
 * await hubSpotService.submitContactForm(formData);
 * await hubSpotService.subscribeToNewsletter(email);
 * ```
 */
export class HubSpotService {
  // Portal ID and form GUID are not secrets (they're visible in HubSpot's own embed code),
  // but they're deliberately env-driven with no hardcoded fallback: the production ID must
  // only ever end up in a production build (REACT_APP_HUBSPOT_*_PRODUCTION in CI). A build
  // that isn't configured — local dev without a .env, or staging before a staging-safe form
  // exists — gets empty strings here and HubSpot rejects the submission (400), rather than
  // silently posting test traffic into the real portal.
  private readonly FORM_ID = process.env.REACT_APP_HUBSPOT_FORM_ID || '';
  private readonly PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID || '';
  // A separate form, so Pawsitive Deck interest is reported apart from contact messages.
  private readonly DECK_INTEREST_FORM_ID = process.env.REACT_APP_HUBSPOT_DECK_INTEREST_FORM_ID || '';
  private readonly BASE_URL = 'https://api.hsforms.com/submissions/v3/integration/submit';

  /**
   * @description Submits contact form data to HubSpot
   * @param formData - Contact form data with first name, last name, email, and message
   * @returns Promise<Response> - HubSpot API response
   * @throws Error when submission fails
   * @api Submits to HubSpot contact form endpoint
   * @validation Validates required fields before submission
   */
  async submitContactForm(formData: ContactFormData): Promise<Response> {
    const hubSpotData: HubSpotFormData = {
      fields: [
        {
          name: 'firstname',
          value: formData.firstName
        },
        {
          name: 'lastname',
          value: formData.lastName
        },
        {
          name: 'email',
          value: formData.email
        },
        {
          name: 'message',
          value: formData.message
        }
      ],
      context: {
        pageUri: window.location.href,
        pageName: document.title
      }
    };

    return await this.submitToHubSpot(hubSpotData);
  }

  /**
   * @description Subscribes email to newsletter in HubSpot
   * @param email - Email address for newsletter subscription
   * @returns Promise<Response> - HubSpot API response
   * @throws Error when subscription fails
   * @api Creates contact in HubSpot form
   * @validation Validates email format before submission
   */
  async subscribeToNewsletter(email: string): Promise<Response> {
    const hubSpotData: HubSpotFormData = {
      fields: [
        {
          name: 'email',
          value: email
        }
      ],
      context: {
        pageUri: window.location.href,
        pageName: document.title
      }
    };

    return await this.submitToHubSpot(hubSpotData);
  }

  /** Whether this build has a Pawsitive Deck interest form to post to. */
  get deckInterestConfigured(): boolean {
    return Boolean(this.PORTAL_ID && this.DECK_INTEREST_FORM_ID);
  }

  /**
   * Records one non-binding Pawsitive Deck interest vote against the voter's email. HubSpot keys
   * contacts by email, so a second vote from the same address updates that contact's
   * `pawsitive_deck_interest` rather than adding a voter, and the tally is one vote per person.
   * `pawsitive_deck_launch_updates` carries the separate, optional consent to launch emails. The
   * HubSpot form must define both properties (README, "Products").
   */
  async submitDeckInterest(choice: string, email: string, launchUpdates: boolean): Promise<Response> {
    return await this.submitToHubSpot(
      {
        fields: [
          { name: 'email', value: email },
          { name: 'pawsitive_deck_interest', value: choice },
          { name: 'pawsitive_deck_launch_updates', value: String(launchUpdates) },
        ],
        context: { pageUri: window.location.href, pageName: document.title },
      },
      this.DECK_INTEREST_FORM_ID
    );
  }

  /**
   * @description Submits data to HubSpot form endpoint
   * @param data - Formatted HubSpot form data
   * @returns Promise<Response> - Raw HubSpot API response
   * @throws Error when API call fails
   * @api Internal method for HubSpot API communication
   * @security Uses CORS-enabled public API endpoint
   */
  private async submitToHubSpot(data: HubSpotFormData, formId = this.FORM_ID): Promise<Response> {
    const url = `${this.BASE_URL}/${this.PORTAL_ID}/${formId}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HubSpot API error: ${response.status} ${response.statusText}`);
    }

    return response;
  }
}

// Export singleton instance
export const hubSpotService = new HubSpotService();